// src/components/product/ProductSelector.tsx
// Searchable product selector for purchase workflow integration
// Allows users to select from product catalog or enter manually

import React, { useState, useEffect, useRef } from 'react';
import { Search, Package, ChevronDown, X, Plus } from 'lucide-react';
import { productCatalogAPI, Product } from '../../services/productCatalogAPI';
import type { CategoryHierarchy } from '../../types/category';
import { logger } from '../../utils/logger';

const selectorLogger = logger.createServiceLogger('ProductSelector');

interface ProductSelectorProps {
  onProductSelected: (product: Product | null) => void;
  onManualEntry: () => void;
  selectedProduct: Product | null;
  categories: CategoryHierarchy[];
  disabled?: boolean;
  placeholder?: string;
}

const ProductSelector: React.FC<ProductSelectorProps> = ({
  onProductSelected,
  onManualEntry,
  selectedProduct,
  categories,
  disabled = false,
  placeholder = 'Search products by name, manufacturer, or model...'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load products on mount
  useEffect(() => {
    loadProducts();
  }, []);

  // Filter products when search term changes
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProducts(products);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = products.filter(p => 
      p.name.toLowerCase().includes(term) ||
      p.manufacturer?.toLowerCase().includes(term) ||
      p.model?.toLowerCase().includes(term)
    );
    setFilteredProducts(filtered);
  }, [searchTerm, products]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    setError(null);
    
    try {
      selectorLogger.debug('Loading active products for selector');
      const productList = await productCatalogAPI.listProducts({ active: true });
      setProducts(productList);
      setFilteredProducts(productList);
      selectorLogger.info('Products loaded for selector', { count: productList.length });
    } catch (err: any) {
      selectorLogger.error('Failed to load products', undefined, err);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (categoryId: string): string => {
    const category = categories.find(c => c.categoryId === categoryId);
    if (!category) return '';
    
    // If it has a parent, find parent name
    if (category.parentCategoryId) {
      const parent = categories.find(c => c.categoryId === category.parentCategoryId);
      return parent ? `${parent.name} › ${category.name}` : category.name;
    }
    
    return category.name;
  };

  const getProductDisplayName = (product: Product): string => {
    let display = product.name;
    if (product.manufacturer || product.model) {
      const details = [product.manufacturer, product.model].filter(Boolean).join(' ');
      display += ` - ${details}`;
    }
    return display;
  };

  const handleSelect = (product: Product) => {
    selectorLogger.debug('Product selected', { productId: product.productId, name: product.name });
    onProductSelected(product);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleClear = () => {
    selectorLogger.debug('Selection cleared');
    onProductSelected(null);
    setSearchTerm('');
  };

  const handleManualEntry = () => {
    selectorLogger.debug('Manual entry selected');
    onManualEntry();
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  // Group products by category for display
  const groupedProducts = filteredProducts.reduce((acc, product) => {
    const categoryName = getCategoryName(product.categoryId) || 'Uncategorized';
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(product);
    return acc;
  }, {} as Record<string, Product[]>);

  // If a product is selected, show the selected state
  if (selectedProduct) {
    return (
      <div className="relative" ref={containerRef}>
        <div className="flex items-center justify-between px-3 py-2 border border-blue-500 rounded-lg bg-blue-50">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <Package className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <div className="truncate">
              <span className="text-sm font-medium text-gray-900">
                {getProductDisplayName(selectedProduct)}
              </span>
              {selectedProduct.typicalUnitCost && (
                <span className="text-xs text-gray-500 ml-2">
                  (${selectedProduct.typicalUnitCost.toFixed(2)})
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={handleClear}
            disabled={disabled}
            className="p-1 hover:bg-blue-100 rounded transition-colors flex-shrink-0"
            title="Clear selection"
          >
            <X className="w-4 h-4 text-blue-600" />
          </button>
        </div>
        <p className="text-xs text-blue-600 mt-1">
          Product selected from catalog - fields auto-populated below
        </p>
      </div>
    );
  }

  return (
    <div className="relative" ref={containerRef}>
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          disabled={disabled}
          placeholder={placeholder}
          className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'
          }`}
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
        >
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
          {loading ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">
              Loading products...
            </div>
          ) : error ? (
            <div className="px-4 py-3 text-sm text-red-500 text-center">
              {error}
              <button 
                onClick={loadProducts}
                className="ml-2 text-blue-600 hover:underline"
              >
                Retry
              </button>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">
              {searchTerm ? 'No products match your search' : 'No products in catalog'}
            </div>
          ) : (
            Object.entries(groupedProducts).map(([categoryName, categoryProducts]) => (
              <div key={categoryName}>
                <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wide sticky top-0">
                  {categoryName}
                </div>
                {categoryProducts.map(product => (
                  <button
                    key={product.productId}
                    type="button"
                    onClick={() => handleSelect(product)}
                    className="w-full px-4 py-2 text-left hover:bg-blue-50 flex items-center justify-between group"
                  >
                    <div className="flex items-center space-x-2 min-w-0 flex-1">
                      <Package className="w-4 h-4 text-gray-400 group-hover:text-blue-600 flex-shrink-0" />
                      <div className="truncate">
                        <span className="text-sm text-gray-900">{product.name}</span>
                        {(product.manufacturer || product.model) && (
                          <span className="text-xs text-gray-500 ml-1">
                            - {[product.manufacturer, product.model].filter(Boolean).join(' ')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        product.productType === 'SERIALIZED' 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {product.productType}
                      </span>
                      {product.typicalUnitCost && (
                        <span className="text-xs text-gray-500">
                          ${product.typicalUnitCost.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ))
          )}

          {/* Manual Entry Option */}
          <div className="border-t border-gray-200">
            <button
              type="button"
              onClick={handleManualEntry}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-2 text-blue-600"
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm font-medium">Create New Product</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductSelector;
